/**
 * Traffic that arrived through Google Ads is not required to broadcast: the
 * call is entered with the camera off and nothing is published until the user
 * turns it on from the call itself.
 *
 * The API only exposes `ad_source` on male accounts, so this is false for
 * everyone else — the camera stays required exactly as before.
 */
export const isGoogleAdsUser = (user) => user?.ad_source === 'google'

/**
 * Whether the camera-off treatment applies: the local control panel — which is
 * nothing but camera controls — is suppressed and the local video box carries a
 * single "Enable my camera" button instead.
 *
 * The three view flags are a union, not a sequence. `isFullScreen` covers the
 * pre-search preview, where the local video is already on screen and already
 * black but nothing has been started yet; `isOnCall` is the only one that still
 * holds once a call is minimized out of fullscreen; `isSearching` covers the
 * queue. Any of them is enough — none of them is required.
 */
export const isCameraOffState = ({ isSearching, isOnCall, isFullScreen, isEnableCamera, user }) =>
  Boolean(isSearching || isOnCall || isFullScreen) && !isEnableCamera && isGoogleAdsUser(user)

/**
 * Whether the camera participation terms still have to be confirmed before the
 * camera may be turned on.
 *
 * `is_camera_consented` is the acceptance recorded on the account, and
 * `isConfirmSearch` stands in for guests, who have no account to record it
 * against and whose acceptance therefore only lasts the session.
 *
 * Google Ads traffic is exempt outright — not deferred. Nothing on any route in
 * asks them: not the dashboard, not the search toggle, not the button inside the
 * call. Their camera goes straight on and no acceptance is recorded for them.
 */
export const needsCameraConsent = ({ user, isConfirmSearch }) =>
  !isGoogleAdsUser(user) && !isConfirmSearch && !user?.is_camera_consented
